import React from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, Clock3, MapPin, Award, CheckCircle2 } from "lucide-react";
import { api, ASSET_URL } from "../lib/api";
import { useSite } from "../lib/site-context";
import { applySeo, injectJsonLd } from "../lib/seo";

const emptyForm = {
  name: "", email: "", company: "", phone: "", billing_address: "",
  certificate_name: "", dietary_notes: "", privacy_accepted: false, terms_accepted: false,
};

const money = (cents, currency = "EUR") =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency }).format((cents || 0) / 100);

export default function AcademyEventPage() {
  const { slug } = useParams();
  const { lang, settings, t } = useSite();
  const [event, setEvent] = React.useState(null);
  const [media, setMedia] = React.useState([]);
  const [form, setForm] = React.useState(emptyForm);
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    Promise.all([api.eventBySlug(slug), api.media("image").catch(() => [])])
      .then(([eventData, mediaData]) => { setEvent(eventData); setMedia(mediaData || []); })
      .catch(() => setEvent(false));
  }, [slug]);

  React.useEffect(() => {
    if (!event) return;
    const base = (settings?.public_base_url || "https://dr-b-digitec.com").replace(/\/$/, "");
    const title = lang === "en" ? (event.title_en || event.title_de) : event.title_de;
    const description = lang === "en" ? (event.excerpt_en || event.excerpt_de) : event.excerpt_de;
    const asset = media.find(m => m.id === event.cover_media_id);
    const imagePath = asset?.variants?.webp || asset?.variants?.original;
    const image = imagePath ? ASSET_URL(imagePath) : undefined;
    const eventEnded = Boolean(event.ends_at && new Date(event.ends_at) < new Date());
    applySeo({
      slug: `akademie/${event.slug}`, lang, baseUrl: base,
      seo: { title: `${title} – Dr. B.-Digitec Akademie`, description, robots: "index,follow" },
      ogImage: image,
    });
    injectJsonLd({
      "@context": "https://schema.org",
      "@graph": [{
        "@type": "EducationEvent",
        "@id": `${base}/akademie/${event.slug}#event`,
        name: title,
        description,
        startDate: event.starts_at,
        endDate: event.ends_at,
        eventStatus: event.status === "cancelled"
          ? "https://schema.org/EventCancelled"
          : eventEnded || ["completed", "ended", "past"].includes(event.status)
            ? "https://schema.org/EventCompleted"
            : "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: {
          "@type": "Place",
          name: event.venue_name,
          address: event.venue_address,
        },
        image: image ? [image] : undefined,
        organizer: { "@type": "Organization", name: "Dr. B.-Digitec GmbH", url: base },
        performer: event.speaker ? { "@type": "Person", name: event.speaker } : undefined,
        offers: {
          "@type": "Offer",
          url: `${base}/akademie/${event.slug}`,
          price: ((event.price_cents || 0) / 100).toFixed(2),
          priceCurrency: event.currency || "EUR",
          availability: event.is_full
            ? "https://schema.org/SoldOut"
            : eventEnded || ["completed", "ended", "past", "cancelled"].includes(event.status)
              ? "https://schema.org/Discontinued"
              : "https://schema.org/InStock",
        },
      }],
    });
    return () => injectJsonLd(null);
  }, [event, media, lang, settings]);

  const setField = (key, value) => setForm(old => ({ ...old, [key]: value }));
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.billing_address || !form.privacy_accepted || !form.terms_accepted) {
      setError("Bitte füllen Sie alle Pflichtfelder aus und bestätigen Sie Datenschutz und Teilnahmebedingungen.");
      return;
    }
    setBusy(true);
    try {
      const response = await api.registerEvent(event.id, {
        ...form,
        certificate_name: form.certificate_name || form.name,
      });
      setResult(response);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Die Anmeldung konnte nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  };

  if (event === false) return <main className="pt-40 pb-24 text-center"><h1 className="brut-h2">Veranstaltung nicht gefunden</h1></main>;
  if (!event) return <main className="pt-40 pb-24 text-center"><p>Lädt …</p></main>;

  const title = lang === "en" ? (event.title_en || event.title_de) : event.title_de;
  const excerpt = lang === "en" ? (event.excerpt_en || event.excerpt_de) : event.excerpt_de;
  const body = lang === "en" ? (event.body_en || event.body_de) : event.body_de;
  const features = lang === "en" ? (event.features_en?.length ? event.features_en : event.features_de) : event.features_de;
  const asset = media.find(m => m.id === event.cover_media_id);
  const imagePath = asset?.variants?.webp || asset?.variants?.original;
  const starts = event.starts_at ? new Date(event.starts_at) : null;
  const ends = event.ends_at ? new Date(event.ends_at) : null;
  const eventIsPast = Boolean((ends || starts) && (ends || starts) < new Date());
  const registrationOpen = ["open", "sold_out"].includes(event.status)
    && (!ends || ends >= new Date())
    && (!event.registration_deadline || new Date(event.registration_deadline) >= new Date());

  if (result) {
    const waitlist = result.status === "waitlist";
    return (
      <main className="pt-40 pb-24">
        <section className="max-w-3xl mx-auto px-5 md:px-10">
          <div className="border-2 border-[var(--ink)] rounded-[28px] p-7 md:p-12 shadow-xl">
            <CheckCircle2 size={48} className="mb-6" />
            <div className="brut-tag mb-4">{waitlist ? "Warteliste" : "Anmeldung erhalten"}</div>
            <h1 className="brut-h2 mb-5">{waitlist ? "Sie stehen auf der Warteliste." : "Bitte schließen Sie Ihre Anmeldung mit der Überweisung ab."}</h1>
            <p className="brut-body mb-6">Ihre Referenz: <strong>{result.reference}</strong></p>
            {!waitlist && result.payment && (
              <div className="bg-[var(--line-soft)] p-6 space-y-2 brut-body">
                <p><strong>Betrag:</strong> {money(result.payment.amount_cents, result.payment.currency)}</p>
                <p><strong>Kontoinhaber:</strong> {result.payment.account_holder}</p>
                <p><strong>IBAN:</strong> {result.payment.iban}</p>
                <p><strong>BIC:</strong> {result.payment.bic}</p>
                <p><strong>Verwendungszweck:</strong> {result.payment.reference}</p>
              </div>
            )}
            <p className="brut-body mt-6">Alle Angaben wurden zusätzlich an Ihre E-Mail-Adresse gesendet.</p>
            <Link to="/wissen/news" className="brut-btn mt-8">Zurück zu Aktuelles</Link>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="academy-event-page pt-32 md:pt-40 pb-24" data-testid="academy-event-page">
      <section className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="bg-[#071f4f] text-white rounded-[28px] p-7 md:p-12 flex flex-col justify-center">
            <div className="brut-tag mb-6 !bg-[var(--accent)] !text-[var(--ink)]">Dr. B.-Digitec Akademie</div>
            <h1 className="brut-h1 mb-6">{title}</h1>
            <p className="brut-body opacity-85 max-w-2xl">{excerpt}</p>
          </div>
          {imagePath && <img src={ASSET_URL(imagePath)} alt={asset?.alt_de || title} className="w-full h-full min-h-[360px] object-cover rounded-[28px]" />}
        </div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-0 border-2 border-[var(--ink)] rounded-[24px] overflow-hidden mt-8">
          <Info icon={CalendarDays} label="Datum" value={starts?.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })} />
          <Info icon={Clock3} label="Uhrzeit" value={starts && ends ? `${starts.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}–${ends.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr` : ""} />
          <Info icon={MapPin} label="Ort" value={`${event.venue_name}${event.venue_address ? ` · ${event.venue_address}` : ""}`} />
          <Info icon={Award} label="Fortbildung" value={`${event.fortbildungspunkte || 0} Fortbildungspunkte`} />
        </div>

        <div className="grid lg:grid-cols-12 gap-10 mt-16">
          <article className={registrationOpen || eventIsPast ? "lg:col-span-7" : "lg:col-span-12 max-w-5xl"}>
            {event.speaker && <><div className="brut-tag mb-4">Referent</div><h2 className="brut-h2 mb-8">{event.speaker}</h2></>}
            <div className="brut-body whitespace-pre-line text-lg leading-relaxed">{body}</div>
            {!!features?.length && <ul className="mt-8 space-y-3">{features.map((f, i) => <li key={i} className="flex gap-3 brut-body"><CheckCircle2 size={20} className="shrink-0 mt-1" /> {f}</li>)}</ul>}
          </article>

          {(registrationOpen || eventIsPast) && <aside className="lg:col-span-5">
            {!registrationOpen ? (
              <div className="border-2 border-[var(--ink)] rounded-[28px] p-6 md:p-8 shadow-xl space-y-4 sticky top-28">
                <div className="brut-tag">{eventIsPast ? t("Veranstaltungsarchiv", "Event archive") : t("Anmeldung in Vorbereitung", "Registration coming soon")}</div>
                <h2 className="brut-h2">{eventIsPast ? t("Die Anmeldung ist geschlossen.", "Registration is closed.") : t("Die Anmeldung wird in Kürze freigeschaltet.", "Registration will open shortly.")}</h2>
                <p className="brut-body">{eventIsPast
                  ? t("Diese Seite bleibt als Information zur Veranstaltung erhalten.", "This page remains available as information about the event.")
                  : t("Der Termin steht bereits fest. Weitere Angaben und die verbindliche Anmeldung werden hier ergänzt, sobald alle Details vorliegen.", "The date is confirmed. Further details and registration will be added here as soon as everything is final.")}</p>
                <Link to="/ueber-uns/veranstaltungen" className="brut-btn">{t("Alle Veranstaltungen", "All events")}</Link>
              </div>
            ) : (
            <form onSubmit={submit} className="border-2 border-[var(--ink)] rounded-[28px] p-6 md:p-8 shadow-xl space-y-4 sticky top-28">
              <div className="brut-tag">Verbindliche Anmeldung</div>
              <h2 className="brut-h2">{money(event.price_cents, event.currency)}</h2>
              <p className="text-sm opacity-70">{event.price_note}</p>
              {event.is_full && <div className="bg-amber-100 border border-amber-500 p-3 text-sm font-bold">Ausgebucht – Anmeldung zur Warteliste</div>}
              <input className="brut-input" placeholder="Vor- und Nachname *" value={form.name} onChange={e => setField("name", e.target.value)} />
              <input type="email" className="brut-input" placeholder="E-Mail *" value={form.email} onChange={e => setField("email", e.target.value)} />
              <input className="brut-input" placeholder="Praxis / Unternehmen" value={form.company} onChange={e => setField("company", e.target.value)} />
              <input className="brut-input" placeholder="Telefon" value={form.phone} onChange={e => setField("phone", e.target.value)} />
              <textarea rows={3} className="brut-textarea" placeholder="Rechnungsanschrift *" value={form.billing_address} onChange={e => setField("billing_address", e.target.value)} />
              <input className="brut-input" placeholder="Name auf Teilnahmebescheinigung" value={form.certificate_name} onChange={e => setField("certificate_name", e.target.value)} />
              <input className="brut-input" placeholder="Ernährungswünsche (optional)" value={form.dietary_notes} onChange={e => setField("dietary_notes", e.target.value)} />
              <label className="flex gap-3 text-sm"><input type="checkbox" checked={form.privacy_accepted} onChange={e => setField("privacy_accepted", e.target.checked)} /><span>Ich habe die <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link> gelesen.</span></label>
              <label className="flex gap-3 text-sm"><input type="checkbox" checked={form.terms_accepted} onChange={e => setField("terms_accepted", e.target.checked)} /><span>Ich akzeptiere die Teilnahme-, Zahlungs- und Stornierungsbedingungen: {event.cancellation_terms_de}</span></label>
              {error && <div className="border-2 border-red-500 p-3 text-sm font-bold">{error}</div>}
              <button disabled={busy} className="brut-btn w-full">{busy ? "Wird angemeldet …" : event.is_full ? "Auf Warteliste eintragen" : `Zahlungspflichtig anmelden – ${money(event.price_cents, event.currency)}`}</button>
            </form>
            )}
          </aside>}
        </div>
      </section>
    </main>
  );
}

function Info({ icon: Icon, label, value }) {
  return <div className="p-5 border-b-2 sm:border-r-2 xl:border-b-0 last:border-r-0 border-[var(--ink)]"><Icon size={22} className="mb-3" /><div className="font-mono text-[10px] uppercase tracking-widest opacity-60">{label}</div><div className="font-bold mt-1">{value || "—"}</div></div>;
}
